import { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, X } from 'lucide-react';
import type { MemberNotification } from '@techsofit/api-types';
import { cn } from '@techsofit/ui';
import {
  getMyNotifications,
  getMyUnreadCount,
  markAllNotificationsRead,
} from '../lib/memberApi';

/** Short relative time like "2h" / "3d" — compact for the feed. */
function ago(iso: string): string {
  const then = new Date(iso).getTime();
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return 'now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d`;
  return `${Math.round(days / 7)}w`;
}

/** Bell + unread badge for the member portal. Polls the unread count; opens a feed panel. */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<MemberNotification[] | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const loadCount = useCallback(() => {
    getMyUnreadCount()
      .then((r) => setUnread(r.count))
      .catch(() => undefined);
  }, []);

  // Poll the badge: on mount, every 30s, and whenever the tab regains focus.
  useEffect(() => {
    loadCount();
    const timer = window.setInterval(loadCount, 30_000);
    const onFocus = () => loadCount();
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [loadCount]);

  // Close the panel on outside click.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  async function toggle() {
    const next = !open;
    setOpen(next);
    if (!next) return;
    setItems(null);
    const list = await getMyNotifications().catch(() => [] as MemberNotification[]);
    setItems(list);
    if (list.some((n) => !n.read)) {
      setUnread(0);
      markAllNotificationsRead().catch(loadCount);
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={toggle}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        className="relative inline-flex size-touch items-center justify-center rounded-control text-muted hover:bg-surface hover:text-ink"
      >
        <Bell className="size-5" aria-hidden />
        {unread > 0 && (
          <span className="absolute right-1 top-1 grid min-w-4 place-items-center rounded-full bg-bad-ink px-1 text-[10px] font-semibold leading-4 text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-20 mt-1 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-card border border-line bg-card shadow-card-hover">
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <p className="font-display text-sm font-semibold text-ink">Notifications</p>
            <button type="button" aria-label="Close" onClick={() => setOpen(false)} className="text-muted hover:text-ink">
              <X className="size-4" aria-hidden />
            </button>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {items === null ? (
              <p className="px-4 py-8 text-center text-sm text-muted">Loading…</p>
            ) : items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-muted">No notifications yet.</p>
            ) : (
              <ul className="flex flex-col divide-y divide-line">
                {items.map((n) => (
                  <li key={n.id} className={cn('flex gap-3 px-4 py-3', !n.read && 'bg-surface')}>
                    <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', n.read ? 'bg-transparent' : 'bg-accent')} aria-hidden />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-ink">{n.title}</p>
                        <span className="shrink-0 text-xs text-muted">{ago(n.createdAt)}</span>
                      </div>
                      {n.body && <p className="mt-0.5 text-sm text-ink-soft">{n.body}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
